import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Shield, Hand, Radio, Radar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { heroData, safetyLevels, carriages, targetAudiences, testimonials } from '../mock';

export const Home = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const iconMap = {
    hand: Hand,
    radio: Radio,
    radar: Radar
  };

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroData.images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">{heroData.title}</h1>
            <p className="hero-subtitle-small">{heroData.subtitle}</p>
            <p className="hero-tagline">{heroData.tagline}</p>
            <p className="hero-description">{heroData.description}</p>
            <div className="hero-actions">
              <Link to="/contact">
                <Button size="lg" className="cta-primary">
                  Demande d'informations
                  <ChevronRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/attelages">
                <Button size="lg" variant="outline" className="cta-secondary">
                  Découvrir nos Attelages
                </Button>
              </Link>
            </div>
          </div>
          <div className="hero-image">
            <img
              src={heroData.images[currentImageIndex]}
              alt="Pégase attelage"
              className="hero-img"
            />
            <div className="image-indicators">
              {heroData.images.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                  aria-label={`Image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="container">
          <h2 className="section-title">Un Trait d'Union entre Vous et la Nature</h2>
          <p className="section-description">
            Pégase rend accessible ce qui ne l'était pas : plages, dunes, mer, océan, chemins naturels,
            réserves... Nos attelages compacts, légers et ultra-sécurisés permettent à tous
            de vivre l'expérience unique de la nature sauvage.
          </p>
        </div>
      </section>

      {/* Safety Section */}
      <section className="safety-section">
        <div className="container">
          <div className="safety-header">
            <Shield className="safety-icon" size={48} />
            <h2 className="section-title">Sécurité : Notre Priorité Absolue</h2>
            <p className="section-description">
              Triple système de sécurité pour libérer instantanément le cheval.
              Zéro stress pour l'animal, protection maximale pour les personnes.
            </p>
          </div>
          <div className="safety-levels">
            {safetyLevels.map((level) => {
              const IconComponent = iconMap[level.icon];
              return (
                <Card key={level.id} className="safety-card">
                  <CardContent className="safety-card-content">
                    <div className="safety-number">Niveau {level.id}</div>
                    <IconComponent className="safety-level-icon" size={40} />
                    <h3 className="safety-level-title">{level.title}</h3>
                    <p className="safety-level-description">{level.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="safety-cta">
            <Link to="/securite">
              <Button size="lg" className="cta-secondary">
                En savoir plus sur notre système de sécurité
                <ChevronRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Carriages Preview */}
      <section className="carriages-preview-section">
        <div className="container">
          <h2 className="section-title">Nos Deux Attelages</h2>
          <div className="carriages-grid">
            {carriages.map((carriage) => (
              <Card key={carriage.id} className="carriage-preview-card">
                <div className="carriage-image-wrapper">
                  <img
                    src={carriage.images[0]}
                    alt={carriage.name}
                    className="carriage-preview-img"
                  />
                </div>
                <CardContent className="carriage-preview-content">
                  <h3 className="carriage-name">{carriage.name}</h3>
                  <p className="carriage-subtitle">{carriage.subtitle}</p>
                  <p className="carriage-description">{carriage.description}</p>
                  <div className="carriage-specs">
                    <span className="spec">Poids: {carriage.weight}</span>
                    <span className="spec">Capacité: {carriage.capacity}</span>
                  </div>
                  <Link to="/attelages">
                    <Button className="mt-4 w-full">
                      Découvrir {carriage.name}
                      <ChevronRight className="ml-2" size={18} />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="audience-section">
        <div className="container">
          <h2 className="section-title">Pour Qui ?</h2>
          <div className="audience-grid">
            {targetAudiences.map((audience) => {
              const iconName = audience.icon;
              const IconComponent = require('lucide-react')[iconName.split('-').map((word, i) => 
                i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join('')];
              
              return (
                <Card key={audience.id} className="audience-card">
                  <CardContent className="audience-card-content">
                    <IconComponent className="audience-icon" size={36} />
                    <h3 className="audience-title">{audience.title}</h3>
                    <p className="audience-description">{audience.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title">Ils Ont Testé Pégase</h2>
          <div className="testimonials-grid">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="testimonial-card">
                <CardContent className="testimonial-content">
                  <p className="testimonial-text">"{testimonial.text}"</p>
                  <div className="testimonial-author">
                    <div className="avatar">{testimonial.avatar}</div>
                    <div>
                      <div className="author-name">{testimonial.name}</div>
                      <div className="author-role">{testimonial.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="container">
          <div className="final-cta-content">
            <h2 className="final-cta-title">Prêt à Découvrir la Nature Sans Limites ?</h2>
            <p className="final-cta-description">
              Demandez des informations ou pré-commandez votre attelage Pégase dès aujourd'hui.
            </p>
            <Link to="/contact">
              <Button size="lg" className="cta-primary-large">
                Demande d'informations / Pré-commander
                <ChevronRight className="ml-2" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
